import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  CanadianWorkersCompensationDto,
  CanadianWorkersCompensationFindResponseDto,
  CanadianWorkersCompensationUpsertRequestDto,
  CompanyVendorDto,
  CompanyVendorFileDto,
  CompanyVendorsAccountingLinkResponseDto,
  CompanyVendorsArchiveClientCreatedResponseDto,
  CompanyVendorsArchiveRequestDto,
  CompanyVendorsCheckTaxIdRequestDto,
  CompanyVendorsCheckTaxIdResponseDto,
  CompanyVendorsComplianceFindingsResponseDto,
  CompanyVendorsConfirmSigningRequestDto,
  CompanyVendorsConfirmSigningResponseDto,
  CompanyVendorsCreateManyNewClientRegisteredVendorsRequestDto,
  CompanyVendorsCreateManyNewExemptVendorsRequestDto,
  CompanyVendorsCreatePaymentSetupResponseDto,
  CompanyVendorsFeaturesControllerFindAccountingLinksV3Params,
  CompanyVendorsFeaturesControllerGetVendorRankV3Params,
  CompanyVendorsFeaturesControllerSetAccountingLinkV3Params,
  CompanyVendorsFeaturesControllerSetVendorRankV3Params,
  CompanyVendorsFeaturesControllerUpdateVendorRecordIssue1099V3Params,
  CompanyVendorsFeeVoucherAllowanceResponseDto,
  CompanyVendorsFindAllByCompanyIdRequestDto,
  CompanyVendorsFindAllByCompanyIdResponseDto,
  CompanyVendorsFindAllRequestDto,
  CompanyVendorsFindAllResponseDto,
  CompanyVendorsFindContactsByCompanyAndVendorResponseDto,
  CompanyVendorsFindOneByCompanyAndClientRegisteredVendorRequestDto,
  CompanyVendorsFindOneByCompanyAndClientRegisteredVendorResponseDto,
  CompanyVendorsFindOneByCompanyAndExemptVendorRequestDto,
  CompanyVendorsFindOneByCompanyAndExemptVendorResponseDto,
  CompanyVendorsFindOneByCompanyAndVendorRequestDto,
  CompanyVendorsFindOneByCompanyAndVendorResponseDto,
  CompanyVendorsGetSigningUrlRequestDto,
  CompanyVendorsInsurancePassResponseDto,
  CompanyVendorsResolvedComplianceSettingRequestDto,
  CompanyVendorsRevokeInsuranceWaiverRequestDto,
  CompanyVendorsRevokeInsuranceWaiverResponseDto,
  CompanyVendorsSaveInsuranceWaiverRequestDto,
  CompanyVendorsSaveInsuranceWaiverResponseDto,
  CompanyVendorsSaveVendorReviewRequestDto,
  CompanyVendorsSetAccountingLinkRequestDto,
  CompanyVendorsSetVendorRankRequestDto,
  CompanyVendorsSignDocumentRequestDto,
  CompanyVendorsSignablePdfRequestDto,
  CompanyVendorsUpdateIssue1099BySubjectResponseDto,
  CompanyVendorsUpdateIssue1099RequestDto,
  CompanyVendorsUpdateIssue1099ResponseDto,
  CompanyVendorsUpdateOneByVendorRequestDto,
  CompanyVendorsUpdateOneRequestDto,
  CompanyVendorsUpdateRelationshipRequestDto,
  CompanyVendorsUpdateRelationshipResponseDto,
  CompanyVendorsUploadDocumentRequestDto,
  CompanyVendorsUploadInsuranceWaiverFileResponseDto,
  CompanyVendorsVendorRankResponseDto,
  CompanyVendorsVendorReviewResponseDto,
  CompanyVendorsW9MassSignTargetsResponseDto,
  ComplianceSettingDto,
  NotesFindAllVendorsRequestDto,
  NotesFindAllVendorsResponseDto,
  SignedDocumentDto,
  SignedDocumentsFindAllRequestDto,
  SignedDocumentsFindAllResponseDto,
  SignedDocumentsGetPresignedUrlsResponseDto,
  SignedDocumentsGetSigningUrlResponseDto,
  VendorDocumentsFindAllRequestDto,
  VendorDocumentsFindAllResponseDto,
  VendorDocumentsGetPresignedUrlResponseDto,
  VendorsCreateRequestDto
} from './vive-api-models';

class CompanyVendorsService extends ViveApiBaseService {
  public async findAll(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<CompanyVendorsFindAllRequestDto, 'includeAssociations'>;
  }): Promise<CompanyVendorsFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<CompanyVendorsFindAllResponseDto>({
      apiEndpoint: 'company-vendors',
      options,
      queryParams
    });
  }

  public async findOneById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyVendorId: number };
  }): Promise<CompanyVendorDto> {
    const { options, pathParams } = params;
    return await this.sendGet<CompanyVendorDto>({
      apiEndpoint: 'company-vendors/:companyVendorId',
      options,
      pathParams
    });
  }

  public async findAllByCompanyId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Omit<
      CompanyVendorsFindAllByCompanyIdRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompanyVendorsFindAllByCompanyIdResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsFindAllByCompanyIdResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId',
      options,
      pathParams,
      queryParams
    });
  }

  public async findAllForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: Omit<
      CompanyVendorsFindAllRequestDto,
      'includeAssociations' | 'vendorId'
    >;
  }): Promise<CompanyVendorsFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsFindAllResponseDto>({
      apiEndpoint: 'vendors/:vendorId/company-vendors',
      options,
      pathParams,
      queryParams
    });
  }

  public async findOneByCompanyAndVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    queryParams?: Omit<
      CompanyVendorsFindOneByCompanyAndVendorRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompanyVendorsFindOneByCompanyAndVendorResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsFindOneByCompanyAndVendorResponseDto>(
      {
        apiEndpoint: 'company-vendors/company/:companyId/vendor/:vendorId',
        options,
        pathParams,
        queryParams
      }
    );
  }

  public async findContactsByCompanyAndVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CompanyVendorsFindContactsByCompanyAndVendorResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<
      CompanyVendorsFindContactsByCompanyAndVendorResponseDto[]
    >({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/contacts',
      options,
      pathParams
    });
  }

  public async createVendorReview(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CompanyVendorsSaveVendorReviewRequestDto;
  }): Promise<CompanyVendorsVendorReviewResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorsVendorReviewResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/reviews',
      options,
      pathParams,
      data
    });
  }

  public async replaceVendorReview(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number; vendorReviewId: number };
    data: CompanyVendorsSaveVendorReviewRequestDto;
  }): Promise<CompanyVendorsVendorReviewResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPut<CompanyVendorsVendorReviewResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/reviews/:vendorReviewId',
      options,
      pathParams,
      data
    });
  }

  public async findOneByCompanyAndExemptVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; exemptVendorId: number };
    queryParams?: Omit<
      CompanyVendorsFindOneByCompanyAndExemptVendorRequestDto,
      never
    >;
  }): Promise<CompanyVendorsFindOneByCompanyAndExemptVendorResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsFindOneByCompanyAndExemptVendorResponseDto>(
      {
        apiEndpoint:
          'company-vendors/company/:companyId/exempt-vendor/:exemptVendorId',
        options,
        pathParams,
        queryParams
      }
    );
  }

  public async findOneByCompanyAndClientRegisteredVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; clientRegisteredVendorId: number };
    queryParams?: Omit<
      CompanyVendorsFindOneByCompanyAndClientRegisteredVendorRequestDto,
      never
    >;
  }): Promise<CompanyVendorsFindOneByCompanyAndClientRegisteredVendorResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsFindOneByCompanyAndClientRegisteredVendorResponseDto>(
      {
        apiEndpoint:
          'company-vendors/company/:companyId/client-registered-vendor/:clientRegisteredVendorId',
        options,
        pathParams,
        queryParams
      }
    );
  }

  public async updateOneById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyVendorId: number };
    data: CompanyVendorsUpdateOneRequestDto;
  }): Promise<CompanyVendorDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<CompanyVendorDto>({
      apiEndpoint: 'company-vendors/:companyVendorId',
      options,
      pathParams,
      data
    });
  }

  public async getComplianceSettingForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<ComplianceSettingDto> {
    const { options, pathParams } = params;
    return await this.sendGet<ComplianceSettingDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/compliance-setting',
      options,
      pathParams
    });
  }

  public async getComplianceFindingsForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CompanyVendorsComplianceFindingsResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<CompanyVendorsComplianceFindingsResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/compliance-findings',
      options,
      pathParams
    });
  }

  public async uploadInsuranceWaiverFile(params: {
    pathParams: { companyId: number; vendorId: number };
    file: File;
  }): Promise<CompanyVendorsUploadInsuranceWaiverFileResponseDto> {
    const { pathParams, file } = params;
    const formData = new FormData();
    formData.append('file', file);
    return await this.sendPost<CompanyVendorsUploadInsuranceWaiverFileResponseDto>(
      {
        apiEndpoint:
          'company-vendors/company/:companyId/vendor/:vendorId/compliance-waivers/file',
        pathParams,
        data: formData
      }
    );
  }

  public async saveInsuranceWaiver(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CompanyVendorsSaveInsuranceWaiverRequestDto;
  }): Promise<CompanyVendorsSaveInsuranceWaiverResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorsSaveInsuranceWaiverResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/compliance-waivers',
      options,
      pathParams,
      data
    });
  }

  public async revokeInsuranceWaiver(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number; waiverId: number };
    data: CompanyVendorsRevokeInsuranceWaiverRequestDto;
  }): Promise<CompanyVendorsRevokeInsuranceWaiverResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorsRevokeInsuranceWaiverResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/compliance-waivers/:waiverId/revoke',
      options,
      pathParams,
      data
    });
  }

  public async getResolvedComplianceSettingForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    queryParams: CompanyVendorsResolvedComplianceSettingRequestDto;
  }): Promise<ComplianceSettingDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<ComplianceSettingDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/resolved-compliance-setting',
      options,
      pathParams,
      // Spread, not cast. A generated DTO is an INTERFACE and so has no
      // implicit index signature; the anonymous object a spread produces does,
      // so this assigns to `Record<string, unknown>` on its own. The request
      // builder only reads `Object.keys` and indexes, so a shallow copy is
      // identical to it.
      queryParams: { ...queryParams }
    });
  }

  public async attachVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: {
      VendorId: number;
      CompanyComplianceTypeId?: number;
      claimCode?: string;
    };
  }): Promise<CompanyVendorDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorDto>({
      apiEndpoint: 'company-vendors/company/:companyId/attach-vendor',
      options,
      pathParams,
      data
    });
  }

  public async createNewVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: {
      vendor: VendorsCreateRequestDto;
      CompanyComplianceTypeId?: number;
      claimCode?: string;
    };
  }): Promise<CompanyVendorDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorDto>({
      apiEndpoint: 'company-vendors/company/:companyId/new-vendor',
      options,
      pathParams,
      data
    });
  }

  public async createManyNewClientRegisteredVendors(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: CompanyVendorsCreateManyNewClientRegisteredVendorsRequestDto;
  }): Promise<CompanyVendorDto[]> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorDto[]>({
      apiEndpoint:
        'company-vendors/company/:companyId/many-new-client-registered-vendors',
      options,
      pathParams,
      data
    });
  }

  public async createManyNewExemptVendors(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: CompanyVendorsCreateManyNewExemptVendorsRequestDto;
  }): Promise<CompanyVendorDto[]> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorDto[]>({
      apiEndpoint: 'company-vendors/company/:companyId/many-new-exempt-vendors',
      options,
      pathParams,
      data
    });
  }

  public async getSigningUrl(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    queryParams?: CompanyVendorsGetSigningUrlRequestDto;
  }): Promise<SignedDocumentsGetSigningUrlResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<SignedDocumentsGetSigningUrlResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/signing-url',
      options,
      pathParams,
      // Guarded so an absent query stays absent: `{ ...undefined }` is `{}`,
      // which is truthy and would take the request builder's query branch.
      queryParams: queryParams && { ...queryParams }
    });
  }

  public async confirmSigning(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    queryParams: CompanyVendorsConfirmSigningRequestDto;
  }): Promise<CompanyVendorsConfirmSigningResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendPost<CompanyVendorsConfirmSigningResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/confirm-signing',
      options,
      pathParams,
      queryParams: { ...queryParams }
    });
  }

  public async updateOneByVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CompanyVendorsUpdateOneByVendorRequestDto;
  }): Promise<CompanyVendorDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<CompanyVendorDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/update-one-by-vendor',
      options,
      pathParams,
      data
    });
  }

  public async updateIssue1099(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CompanyVendorsUpdateIssue1099RequestDto;
  }): Promise<CompanyVendorsUpdateIssue1099ResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<CompanyVendorsUpdateIssue1099ResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/issue-1099',
      options,
      pathParams,
      data
    });
  }

  public async updateVendorRecordIssue1099(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams: Omit<
      CompanyVendorsFeaturesControllerUpdateVendorRecordIssue1099V3Params,
      'companyId'
    >;
    data: CompanyVendorsUpdateIssue1099RequestDto;
  }): Promise<CompanyVendorsUpdateIssue1099BySubjectResponseDto> {
    const { options, pathParams, queryParams, data } = params;
    return await this.sendPatch<CompanyVendorsUpdateIssue1099BySubjectResponseDto>(
      {
        apiEndpoint: 'company-vendors/company/:companyId/issue-1099',
        options,
        pathParams,
        queryParams,
        data
      }
    );
  }

  public async findAllDocuments(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    // `CompanyId` and `VendorId` come from the path and the route overrides
    // whatever the query carries, so a caller has no business sending them.
    queryParams?: Omit<
      VendorDocumentsFindAllRequestDto,
      'CompanyId' | 'VendorId'
    >;
  }): Promise<VendorDocumentsFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<VendorDocumentsFindAllResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/documents',
      options,
      pathParams,
      queryParams
    });
  }

  public async uploadDocument(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CompanyVendorsUploadDocumentRequestDto;
    file: File;
  }): Promise<CompanyVendorFileDto> {
    const { options, pathParams, data, file } = params;
    const formData = new FormData();
    formData.append('docType', data.docType);
    formData.append('file', file);
    return await this.sendPost<CompanyVendorFileDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/documents',
      options,
      pathParams,
      data: formData
    });
  }

  public async findAllSignedDocuments(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    // `CompanyId` and `VendorId` come from the path and the route overrides
    // whatever the query carries, so a caller has no business sending them.
    queryParams?: Omit<
      SignedDocumentsFindAllRequestDto,
      'CompanyId' | 'VendorId'
    >;
  }): Promise<SignedDocumentsFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<SignedDocumentsFindAllResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/signed-documents',
      options,
      pathParams,
      queryParams
    });
  }

  public async getSignedDocumentPresignedUrls(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      companyId: number;
      vendorId: number;
      signedDocumentId: number;
    };
  }): Promise<SignedDocumentsGetPresignedUrlsResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<SignedDocumentsGetPresignedUrlsResponseDto[]>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/signed-documents/:signedDocumentId/presigned-urls',
      options,
      pathParams
    });
  }

  public async findAllNotes(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    queryParams?: Omit<NotesFindAllVendorsRequestDto, 'includeAssociations'>;
  }): Promise<NotesFindAllVendorsResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<NotesFindAllVendorsResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId/vendor/:vendorId/notes',
      options,
      pathParams,
      queryParams
    });
  }

  public async getFeeVoucherAllowance(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
  }): Promise<CompanyVendorsFeeVoucherAllowanceResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<CompanyVendorsFeeVoucherAllowanceResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId/fee-vouchers',
      options,
      pathParams
    });
  }

  public async updateRelationship(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CompanyVendorsUpdateRelationshipRequestDto;
  }): Promise<CompanyVendorsUpdateRelationshipResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<CompanyVendorsUpdateRelationshipResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId/vendor/:vendorId',
      options,
      pathParams,
      data
    });
  }

  public async getVendorRank(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams: Omit<
      CompanyVendorsFeaturesControllerGetVendorRankV3Params,
      'companyId'
    >;
  }): Promise<CompanyVendorsVendorRankResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsVendorRankResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId/rank',
      options,
      pathParams,
      queryParams
    });
  }

  public async setVendorRank(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams: Omit<
      CompanyVendorsFeaturesControllerSetVendorRankV3Params,
      'companyId'
    >;
    data: CompanyVendorsSetVendorRankRequestDto;
  }): Promise<CompanyVendorsVendorRankResponseDto> {
    const { options, pathParams, queryParams, data } = params;
    return await this.sendPut<CompanyVendorsVendorRankResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId/rank',
      options,
      pathParams,
      queryParams,
      data
    });
  }

  public async findAccountingLinks(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams: Omit<
      CompanyVendorsFeaturesControllerFindAccountingLinksV3Params,
      'companyId'
    >;
  }): Promise<CompanyVendorsAccountingLinkResponseDto[]> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsAccountingLinkResponseDto[]>({
      apiEndpoint: 'company-vendors/company/:companyId/accounting-links',
      options,
      pathParams,
      queryParams
    });
  }

  public async setAccountingLink(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; apVendorId: number };
    queryParams: Omit<
      CompanyVendorsFeaturesControllerSetAccountingLinkV3Params,
      'companyId' | 'apVendorId'
    >;
    data: CompanyVendorsSetAccountingLinkRequestDto;
  }): Promise<CompanyVendorsAccountingLinkResponseDto> {
    const { options, pathParams, queryParams, data } = params;
    return await this.sendPatch<CompanyVendorsAccountingLinkResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/accounting-links/:apVendorId',
      options,
      pathParams,
      queryParams,
      data
    });
  }

  public async requestAccountingSync(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; apVendorId: number };
  }): Promise<CompanyVendorsAccountingLinkResponseDto> {
    const { options, pathParams } = params;
    return await this.sendPost<CompanyVendorsAccountingLinkResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/accounting-links/:apVendorId/sync',
      options,
      pathParams
    });
  }

  public async getDocumentPresignedUrl(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      companyId: number;
      vendorId: number;
      vendorDocumentId: number;
    };
  }): Promise<VendorDocumentsGetPresignedUrlResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorDocumentsGetPresignedUrlResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/documents/:vendorDocumentId/presigned-url',
      options,
      pathParams
    });
  }

  public async confirmInsuranceRequirements(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CompanyVendorDto> {
    const { options, pathParams } = params;
    return await this.sendPost<CompanyVendorDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/confirm-insurance-requirements',
      options,
      pathParams
    });
  }

  /**
   * VPDW-4130: force every non-terminal insurance certificate on the
   * relationship to Accepted and pass the Insurance step. VIVE admin and
   * insurance admin only.
   */
  public async passInsurance(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CompanyVendorsInsurancePassResponseDto> {
    const { options, pathParams } = params;
    return await this.sendPost<CompanyVendorsInsurancePassResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/insurance/pass',
      options,
      pathParams
    });
  }

  public async getCanadianWorkersCompensation(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CanadianWorkersCompensationFindResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<CanadianWorkersCompensationFindResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/canadian-workers-compensation',
      options,
      pathParams
    });
  }

  public async upsertCanadianWorkersCompensation(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
    data: CanadianWorkersCompensationUpsertRequestDto;
  }): Promise<CanadianWorkersCompensationDto[]> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CanadianWorkersCompensationDto[]>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/canadian-workers-compensation',
      options,
      pathParams,
      data
    });
  }

  public async createPaymentSetup(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CompanyVendorsCreatePaymentSetupResponseDto> {
    const { options, pathParams } = params;
    return await this.sendPost<CompanyVendorsCreatePaymentSetupResponseDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/payment-setup',
      options,
      pathParams
    });
  }

  public async resumeRegistration(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; vendorId: number };
  }): Promise<CompanyVendorDto> {
    const { options, pathParams } = params;
    return await this.sendPost<CompanyVendorDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/resume-registration',
      options,
      pathParams
    });
  }

  public async getSignablePDF(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      companyId: number;
      vendorId: number;
      esignTemplateId: number;
    };
  }): Promise<Blob> {
    const { options, pathParams } = params;
    return await this.sendGet<Blob>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/esign-template/:esignTemplateId/signable-pdf',
      options,
      pathParams
    });
  }

  /**
   * The POST form, for a client template that declares fields only the vendor
   * can answer. A template with none still uses `getSignablePDF` above.
   */
  public async getSignablePDFWithVendorInput(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      companyId: number;
      vendorId: number;
      esignTemplateId: number;
    };
    data: CompanyVendorsSignablePdfRequestDto;
  }): Promise<Blob> {
    const { options, pathParams, data } = params;
    return await this.sendPost<Blob>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/esign-template/:esignTemplateId/signable-pdf',
      data,
      options,
      pathParams
    });
  }

  public async archiveClientCreatedRelationship(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyVendorId: number };
    data: CompanyVendorsArchiveRequestDto;
  }): Promise<CompanyVendorsArchiveClientCreatedResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorsArchiveClientCreatedResponseDto>({
      apiEndpoint: 'company-vendors/:companyVendorId/archive-client-created',
      options,
      pathParams,
      data
    });
  }

  public async checkTaxId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: CompanyVendorsCheckTaxIdRequestDto;
  }): Promise<CompanyVendorsCheckTaxIdResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompanyVendorsCheckTaxIdResponseDto>({
      apiEndpoint: 'company-vendors/company/:companyId/check-tax-id',
      options,
      pathParams,
      data
    });
  }

  public async signDocument(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      companyId: number;
      vendorId: number;
      esignTemplateId: number;
    };
    data: CompanyVendorsSignDocumentRequestDto;
  }): Promise<SignedDocumentDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<SignedDocumentDto>({
      apiEndpoint:
        'company-vendors/company/:companyId/vendor/:vendorId/esign-template/:esignTemplateId/sign-document',
      options,
      pathParams,
      data
    });
  }
  public async findW9MassSignTargets(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams: { signedCompanyId: number };
  }): Promise<CompanyVendorsW9MassSignTargetsResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyVendorsW9MassSignTargetsResponseDto>({
      apiEndpoint: 'company-vendors/vendor/:vendorId/w9-mass-sign-targets',
      options,
      pathParams,
      queryParams
    });
  }
}

export const companyVendorsService = new CompanyVendorsService();
